import dayjs from 'dayjs';
import z from 'zod';

import type { RegulatoryScheme } from './shared.js';
import {
  type Country,
  ZodCountry,
  ZodRegulatoryScheme,
} from './shared.js';

export interface Batch {
  companyName: string;
  fuelProviderCompanyName: string;
  leaveDate: string;
  batchNumber: string;
  productionYear: number;
  upliftCountry?: Country;
  upliftAirport?: string;
  regulatorySchemesDeclaration: RegulatoryScheme[];

  /**
   * [Issuance V2] Introduced in 03.2026 - older certificates will have this fields missing
   * @NOTE this field can be used to identify if this is new certificate (after 03.2026) or older one
   */
  additionalityApproach: BatchAdditionalityApproach;
}

export enum AdditionalityApproachType {
  SABA = 'saba',
  NonSABA = 'nonSaba',
}

export const AdditionalityApproachTypeToLabel: Record<AdditionalityApproachType, string> = {
  [AdditionalityApproachType.NonSABA]: 'Non-SABA additionality approach',
  [AdditionalityApproachType.SABA]: 'SABA additionality approach',
};

export type BatchAdditionalityApproachNonSaba = { type: AdditionalityApproachType.NonSABA; allowScope3Claims: boolean; approachName: string };
export type BatchAdditionalityApproach
  = | { type: AdditionalityApproachType.SABA }
    | BatchAdditionalityApproachNonSaba;

export const ZodBatchAdditionalityApproach = z.discriminatedUnion('type', [
  z.object({ type: z.literal(AdditionalityApproachType.SABA) }),
  z.object({
    type: z.literal(AdditionalityApproachType.NonSABA),
    approachName: z.string().min(1),
    allowScope3Claims: z.boolean(),
  }),
]);

export const ZodBatch = z.object({
  companyName: z.string().min(1),
  fuelProviderCompanyName: z.string().min(1),
  // rulebook defines 90-days, but we accept 366 days just in case of some timezone differences etc
  leaveDate: z.iso.date().refine((date) => {
    const leaveDate = dayjs(date, 'YYYY-MM-DD', true);
    const minDate = dayjs().subtract(366, 'day').startOf('day');

    return leaveDate.isAfter(minDate, 'day') || leaveDate.isSame(minDate, 'day');
  }, { error: 'Must be at most 366-days in the past' }),
  batchNumber: z.string().min(1),
  productionYear: z.int().refine(year => year <= new Date().getFullYear(), { error: 'Year must be at most current year' }),
  upliftCountry: ZodCountry.optional(),
  upliftAirport: z.string().min(1).optional(),
  regulatorySchemesDeclaration: z.array(ZodRegulatoryScheme).min(1),
  additionalityApproach: ZodBatchAdditionalityApproach,
});
